import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import { useGetHarborOperatorDashboardQuery } from '@/services/HarborOperatorDashboardService';
import { useLazyGetAllBookingsQuery } from '@/services/BookingService';
import {
  useGetMediationCasesQuery,
  useLazyGetMediationCasesQuery,
} from '@/services/MediationService';
import {
  BERTH_CONFLICT_STATUSES,
  BERTH_CONFLICTS_PATH,
  BookingDrillDownView,
  getBerthConflictCasePath,
  getBookingDrillDownParams,
} from '@/pages/booking-management/bookingDrillDowns';
import { BookingStatus } from '@/types/booking-management.types';
import { formatFinnishCurrency } from '@/utils';
import { formatDisplayDate } from '@/utils/Date';

import {
  HarborDashboardStatusDonut,
  HarborDashboardTopHarborsChart,
  HarborDashboardTrendChart,
} from './HarborRoleDashboardCharts';
import { getBookingStatusMessageId } from './harborDashboardStatus';

const DASHBOARD_PATH = '/';

export type HarborDashboardVariant = 'marina' | 'regional';

interface QuickAction {
  labelId: string;
  path: string;
  icon: string;
}

const QUICK_ACTIONS: Record<HarborDashboardVariant, QuickAction[]> = {
  marina: [
    { labelId: 'DASHBOARD.QUICK_ACTIONS.HARBOR_MANAGEMENT', path: '/habor-management', icon: 'logistic' },
    {
      labelId: 'DASHBOARD.QUICK_ACTIONS.BOOKING_MANAGEMENT',
      path: '/booking-management',
      icon: 'calendar',
    },
    {
      labelId: 'DASHBOARD.QUICK_ACTIONS.SUBSCRIPTION_TRANSACTIONS',
      path: '/subscription-transactions',
      icon: 'bill',
    },
    {
      labelId: 'DASHBOARD.QUICK_ACTIONS.NOTIFICATIONS',
      path: '/notification-management',
      icon: 'notification-status',
    },
  ],
  regional: [
    { labelId: 'DASHBOARD.QUICK_ACTIONS.HARBOR_MANAGEMENT', path: '/habor-management', icon: 'logistic' },
    {
      labelId: 'DASHBOARD.QUICK_ACTIONS.BOOKING_MANAGEMENT',
      path: '/booking-management',
      icon: 'calendar',
    },
    {
      labelId: 'DASHBOARD.QUICK_ACTIONS.SUBSCRIPTION_TRANSACTIONS',
      path: '/subscription-transactions',
      icon: 'bill',
    },
    {
      labelId: 'DASHBOARD.QUICK_ACTIONS.SERVICE_PROVIDER_MANAGEMENT',
      path: '/service-provider-management',
      icon: 'category',
    },
    {
      labelId: 'DASHBOARD.QUICK_ACTIONS.COMMUNITY_MANAGEMENT',
      path: '/community-management',
      icon: 'people',
    },
    {
      labelId: 'DASHBOARD.QUICK_ACTIONS.NOTIFICATIONS',
      path: '/notification-management',
      icon: 'notification-status',
    },
  ],
};

const HARBOR_STAT_LABELS: Record<
  HarborDashboardVariant,
  { titleId: string; descriptionId: string }
> = {
  marina: {
    titleId: 'DASHBOARD.CARD.MY_HARBORS',
    descriptionId: 'DASHBOARD.CARD.MY_HARBORS_DESC',
  },
  regional: {
    titleId: 'DASHBOARD.CARD.REGION_HARBORS',
    descriptionId: 'DASHBOARD.CARD.REGION_HARBORS_DESC',
  },
};

const getStatusClassName = (status: number): string => {
  switch (status) {
    case BookingStatus.CONFIRMED:
      return 'text-green-700 dark:text-green-400 contrast:text-green-300';
    case BookingStatus.CANCELLED:
      return 'text-red-600 dark:text-red-400 contrast:text-red-300';
    case BookingStatus.COMPLETED:
      return 'text-blue-600 dark:text-blue-400 contrast:text-blue-300';
    case BookingStatus.PENDING_CONFIRMATION:
      return 'text-gray-600 dark:text-white/70 contrast:text-white';
    default:
      return 'text-yellow-800 dark:text-amber-400 contrast:text-yellow-300';
  }
};

interface HarborRoleDashboardProps {
  variant: HarborDashboardVariant;
}

/** Router state telling a record page the user came from the dashboard, so its back button returns here. */
const DASHBOARD_ORIGIN = { from: DASHBOARD_PATH };
/** Lists opened from the dashboard carry this in the URL, so their back button survives opening a record and returning. */
const DASHBOARD_ORIGIN_PARAM = 'origin=dashboard';

/** Months of booking and revenue history the dashboard asks the API for. */
const TREND_MONTHS = 6;
/** The "My Harbors" card is only useful when the dashboard scope has more than one harbour. */
const MIN_HARBORS_FOR_HARBOR_CARD = 2;
const HARBOR_SEARCH_THRESHOLD = 8;
const DEFAULT_VISIBLE_HARBORS = 10;

const HarborRoleDashboard = ({ variant }: HarborRoleDashboardProps) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const isMarina = variant === 'marina';
  const isRegional = variant === 'regional';
  const [harborSearch, setHarborSearch] = useState('');
  const [showAllHarbors, setShowAllHarbors] = useState(false);

  const { data, isLoading, isError, isFetching } =
    useGetHarborOperatorDashboardQuery({ trendMonths: TREND_MONTHS });

  const { data: mediationData } = useGetMediationCasesQuery({
    status: BERTH_CONFLICT_STATUSES,
    page: 1,
    limit: 5,
  });
  const openMediationCount = mediationData?.total ?? 0;

  const [fetchBookings] = useLazyGetAllBookingsQuery();
  const [fetchMediationCases] = useLazyGetMediationCasesQuery();

  const showDrillDownError = useCallback(
    (err: unknown) => {
      const apiErr = err as { data?: { message?: string } };
      enqueueSnackbar(
        apiErr?.data?.message ??
          intl.formatMessage({ id: 'DASHBOARD.DRILL_DOWN_ERROR' }),
        { variant: 'error' }
      );
    },
    [enqueueSnackbar, intl]
  );

  // Opens the list behind a card; when the card counts exactly one record, that record opens directly.
  const openBookingDrillDown = useCallback(
    async (view: BookingDrillDownView, count: number) => {
      if (count === 1) {
        try {
          const result = await fetchBookings({
            page: 1,
            limit: 1,
            ...getBookingDrillDownParams(view),
          }).unwrap();
          const bookings = result.data ?? result.items ?? [];

          if (result.total === 1 && bookings[0]?.id) {
            navigate(`/booking-management/${bookings[0].id}`, {
              state: DASHBOARD_ORIGIN,
            });
            return;
          }
        } catch (err) {
          showDrillDownError(err);
        }
      }

      navigate(
        `/booking-management?view=${view}&${DASHBOARD_ORIGIN_PARAM}`
      );
    },
    [fetchBookings, navigate, showDrillDownError]
  );

  const openBerthConflicts = useCallback(async () => {
    if (openMediationCount === 1) {
      try {
        const result = await fetchMediationCases({
          status: BERTH_CONFLICT_STATUSES,
          page: 1,
          limit: 1,
        }).unwrap();
        const mediationCase = result.data?.[0];
        const bookingId =
          mediationCase?.requesting_booking_id ??
          mediationCase?.conflicting_booking_id;

        if (result.total === 1 && mediationCase && bookingId) {
          navigate(getBerthConflictCasePath(bookingId, mediationCase.id), {
            state: DASHBOARD_ORIGIN,
          });
          return;
        }
      } catch (err) {
        showDrillDownError(err);
      }
    }

    navigate(BERTH_CONFLICTS_PATH);
  }, [fetchMediationCases, navigate, openMediationCount, showDrillDownError]);

  const summary = data?.summary;
  const revenueTrend = useMemo(
    () => data?.revenue_trend ?? [],
    [data?.revenue_trend]
  );
  const loading = isLoading || isFetching;
  const harborLabels = HARBOR_STAT_LABELS[variant];
  const quickActions = QUICK_ACTIONS[variant];
  const hasNoHarbors = !loading && (summary?.harbor_count ?? 0) === 0;
  const showHarborCard =
    (summary?.harbor_count ?? 0) >= MIN_HARBORS_FOR_HARBOR_CARD;
  const harborCount = data?.harbors?.length ?? 0;
  const shouldShowHarborFilters = harborCount > HARBOR_SEARCH_THRESHOLD;

  const filteredHarbors = useMemo(() => {
    const harbors = data?.harbors ?? [];
    const query = harborSearch.trim().toLowerCase();

    if (!query) {
      return harbors;
    }

    return harbors.filter((harbor) =>
      harbor.name.toLowerCase().includes(query)
    );
  }, [data?.harbors, harborSearch]);

  const visibleHarbors = useMemo(() => {
    if (showAllHarbors || filteredHarbors.length <= DEFAULT_VISIBLE_HARBORS) {
      return filteredHarbors;
    }

    return filteredHarbors.slice(0, DEFAULT_VISIBLE_HARBORS);
  }, [filteredHarbors, showAllHarbors]);

  if (isError) {
    return (
      <Container>
        <div className="card card-grid">
          <div className="card-body">
            <p className="text-sm text-red-600">
              {intl.formatMessage({ id: 'DASHBOARD.LOAD_ERROR' })}
            </p>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="p-0">
      <section
        className="space-y-6"
        aria-label={intl.formatMessage({
          id: isMarina ? 'DASHBOARD.ARIA.MARINA' : 'DASHBOARD.ARIA.REGIONAL',
        })}
      >
        {hasNoHarbors && (
          <div className="card rounded-lg border border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-white dark:from-transparent dark:to-transparent contrast:from-transparent contrast:to-transparent">
            <div className="card-body py-8 text-center">
              <KeenIcon
                icon="logistic"
                className="text-3xl text-primary mx-auto mb-3"
              />
              <p className="text-sm text-gray-700 font-medium">
                {intl.formatMessage({
                  id: isMarina ? 'DASHBOARD.EMPTY.MARINA' : 'DASHBOARD.EMPTY.REGIONAL',
                })}
              </p>
              <Link
                to="/habor-management"
                className="btn btn-sm btn-primary mt-4"
              >
                {intl.formatMessage({ id: 'DASHBOARD.GO_TO_HARBOR_MANAGEMENT' })}
              </Link>
            </div>
          </div>
        )}

        <div
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4"
          role="region"
          aria-label={intl.formatMessage({ id: 'DASHBOARD.ARIA.KPI' })}
        >
          {showHarborCard && (
            <DashboardStatCard
              title={intl.formatMessage({ id: harborLabels.titleId })}
              value={summary?.harbor_count ?? 0}
              description={intl.formatMessage({ id: harborLabels.descriptionId })}
              icon="logistic"
              loading={loading}
              variant="primary"
            />
          )}
          <DashboardStatCard
            title={intl.formatMessage({ id: 'DASHBOARD.CARD.NEEDS_ATTENTION' })}
            value={summary?.needs_attention_bookings ?? 0}
            description={intl.formatMessage({
              id: 'DASHBOARD.CARD.NEEDS_ATTENTION_DESC',
            })}
            icon="information-2"
            loading={loading}
            highlight={(summary?.needs_attention_bookings ?? 0) > 0}
            onClick={() =>
              openBookingDrillDown(
                'needs-attention',
                summary?.needs_attention_bookings ?? 0
              )
            }
          />
          <DashboardStatCard
            title={intl.formatMessage({ id: 'DASHBOARD.CARD.BERTH_CONFLICTS' })}
            value={openMediationCount}
            description={intl.formatMessage({
              id: 'DASHBOARD.CARD.BERTH_CONFLICTS_DESC',
            })}
            icon="information"
            loading={loading}
            highlight={openMediationCount > 0}
            onClick={openBerthConflicts}
          />
          <DashboardStatCard
            title={intl.formatMessage({ id: 'DASHBOARD.UPCOMING_STAYS' })}
            value={summary?.upcoming_bookings ?? 0}
            description={intl.formatMessage({
              id: 'DASHBOARD.CARD.UPCOMING_STAYS_DESC',
            })}
            icon="calendar-tick"
            loading={loading}
            onClick={() =>
              openBookingDrillDown(
                'upcoming-stays',
                summary?.upcoming_bookings ?? 0
              )
            }
          />
          <DashboardStatCard
            title={intl.formatMessage(
              { id: 'DASHBOARD.NET_REVENUE_WINDOW' },
              { months: TREND_MONTHS }
            )}
            value={summary?.net_revenue ?? 0}
            displayValue={
              loading
                ? undefined
                : formatFinnishCurrency(summary?.net_revenue ?? 0)
            }
            description={intl.formatMessage({
              id: 'DASHBOARD.CARD.NET_REVENUE_DESC',
            })}
            icon="wallet"
            loading={loading}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <DashboardStatCard
            title={intl.formatMessage({ id: 'DASHBOARD.CARD.ACTIVE_BOOKINGS' })}
            value={summary?.active_bookings ?? 0}
            description={intl.formatMessage({
              id: 'DASHBOARD.CARD.ACTIVE_BOOKINGS_DESC',
            })}
            icon="timer"
            loading={loading}
            onClick={() =>
              openBookingDrillDown(
                'active-bookings',
                summary?.active_bookings ?? 0
              )
            }
          />
          <DashboardStatCard
            title={intl.formatMessage({ id: 'DASHBOARD.CARD.TOTAL_BERTHS' })}
            value={summary?.total_berths ?? 0}
            description={intl.formatMessage({
              id: 'DASHBOARD.CARD.TOTAL_BERTHS_DESC',
            })}
            icon="category"
            loading={loading}
          />
          <DashboardStatCard
            title={intl.formatMessage({ id: 'DASHBOARD.BOOKED_NIGHTS' })}
            value={summary?.total_booking_nights ?? 0}
            description={intl.formatMessage({
              id: 'DASHBOARD.CARD.BOOKED_NIGHTS_DESC',
            })}
            icon="moon"
            loading={loading}
          />
          <DashboardStatCard
            title={intl.formatMessage({ id: 'DASHBOARD.CARD.COMPLETED' })}
            value={summary?.completed_bookings ?? 0}
            description={intl.formatMessage({
              id: 'DASHBOARD.CARD.COMPLETED_DESC',
            })}
            icon="check-circle"
            loading={loading}
          />
        </div>

        {data?.harbors && data.harbors.length > 0 && (
          <div className="card rounded-lg border border-gray-500 shadow-md p-4">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  {intl.formatMessage({ id: 'DASHBOARD.HARBORS.TITLE' })}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {shouldShowHarborFilters
                    ? intl.formatMessage(
                        { id: 'DASHBOARD.HARBORS.SHOWING' },
                        {
                          visible: visibleHarbors.length,
                          total: filteredHarbors.length,
                        }
                      )
                    : intl.formatMessage({ id: 'DASHBOARD.HARBORS.QUICK_ACCESS' })}
                </p>
              </div>
              {shouldShowHarborFilters && (
                <div className="flex w-full sm:w-auto items-center gap-2">
                  <label className="input input-sm input-bordered flex items-center gap-2 w-full sm:w-72 bg-gray-100">
                    <KeenIcon
                      icon="magnifier"
                      className="text-sm text-gray-500"
                    />
                    <input
                      type="text"
                      value={harborSearch}
                      onChange={(event) => {
                        setHarborSearch(event.target.value);
                        setShowAllHarbors(false);
                      }}
                      placeholder={intl.formatMessage({
                        id: 'DASHBOARD.HARBORS.SEARCH',
                      })}
                      className="grow text-sm"
                      aria-label={intl.formatMessage({
                        id: 'DASHBOARD.HARBORS.SEARCH',
                      })}
                    />
                  </label>
                  {harborSearch && (
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => {
                        setHarborSearch('');
                        setShowAllHarbors(false);
                      }}
                    >
                      {intl.formatMessage({ id: 'DASHBOARD.HARBORS.CLEAR' })}
                    </button>
                  )}
                </div>
              )}
            </div>
            {filteredHarbors.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">
                {intl.formatMessage({ id: 'DASHBOARD.HARBORS.NO_MATCH' })}
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {visibleHarbors.map((harbor) => (
                    <Link
                      key={harbor.id}
                      to="/habor-management"
                      className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800 hover:border-primary hover:text-primary transition-colors"
                    >
                      <KeenIcon icon="geolocation" className="text-sm" />
                      {harbor.name}
                    </Link>
                  ))}
                </div>
                {filteredHarbors.length > DEFAULT_VISIBLE_HARBORS && (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => setShowAllHarbors((current) => !current)}
                    >
                      {showAllHarbors
                        ? intl.formatMessage({ id: 'DASHBOARD.HARBORS.SHOW_FEWER' })
                        : intl.formatMessage(
                            { id: 'DASHBOARD.HARBORS.SHOW_ALL' },
                            { count: filteredHarbors.length }
                          )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div
          className="card rounded-lg border border-gray-500 shadow-md p-4"
          role="region"
          aria-label={intl.formatMessage({
            id: 'DASHBOARD.QUICK_ACTIONS.TITLE',
          })}
        >
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                {intl.formatMessage({ id: 'DASHBOARD.QUICK_ACTIONS.TITLE' })}
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                {intl.formatMessage({ id: 'DASHBOARD.QUICK_ACTIONS.SUBTITLE' })}
              </p>
            </div>
            {shouldShowHarborFilters && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                {intl.formatMessage(
                  { id: 'DASHBOARD.HARBORS.AVAILABLE' },
                  { count: harborCount }
                )}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            {quickActions.map((action) => (
              <Link
                key={action.path}
                to={action.path}
                className="btn btn-sm btn-light inline-flex items-center gap-2"
              >
                <KeenIcon icon={action.icon} className="text-base" />
                {intl.formatMessage({ id: action.labelId })}
              </Link>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <HarborDashboardTrendChart
            title={intl.formatMessage({ id: 'DASHBOARD.NEW_BOOKINGS' })}
            subtitle={intl.formatMessage(
              { id: 'DASHBOARD.CHART.NEW_BOOKINGS_SUBTITLE' },
              { months: TREND_MONTHS }
            )}
            data={data?.bookings_trend ?? []}
            mode="bookings"
            loading={loading}
          />
          <HarborDashboardTrendChart
            title={intl.formatMessage({ id: 'DASHBOARD.CHART.REVENUE_TREND' })}
            subtitle={intl.formatMessage(
              { id: 'DASHBOARD.CHART.REVENUE_SUBTITLE' },
              { months: TREND_MONTHS }
            )}
            data={revenueTrend}
            mode="revenue"
            loading={loading}
          />
        </div>

        <div
          className={`grid grid-cols-1 gap-4 ${isRegional ? 'lg:grid-cols-2' : ''}`}
        >
          <HarborDashboardStatusDonut
            items={data?.booking_status_breakdown ?? []}
            loading={loading}
          />
          {isRegional && (
            <HarborDashboardTopHarborsChart
              items={data?.top_harbors ?? []}
              months={TREND_MONTHS}
              loading={loading}
            />
          )}
        </div>

        <div className="card card-grid rounded-lg border border-gray-500 text-gray-900 shadow-md p-2">
          <div className="card-header flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="card-title text-base font-semibold text-gray-900 dark:text-white">
                {intl.formatMessage({ id: 'DASHBOARD.RECENT.TITLE' })}
              </h2>
              <p className="text-xs text-gray-500 dark:text-white/60">
                {intl.formatMessage({ id: 'DASHBOARD.RECENT.SUBTITLE' })}
              </p>
            </div>
            <Link
              to={`/booking-management?${DASHBOARD_ORIGIN_PARAM}`}
              className="text-xs text-primary dark:text-blue-400 contrast:text-blue-300 font-medium hover:underline"
            >
              {intl.formatMessage({ id: 'DASHBOARD.RECENT.VIEW_ALL' })}
            </Link>
          </div>
          <div className="card-body overflow-x-auto">
            {loading ? (
              <p className="text-sm text-gray-600">
                {intl.formatMessage({ id: 'DASHBOARD.RECENT.LOADING' })}
              </p>
            ) : (data?.recent_bookings?.length ?? 0) === 0 ? (
              <p className="text-sm text-gray-600">
                {intl.formatMessage({ id: 'DASHBOARD.RECENT.EMPTY' })}
              </p>
            ) : (
              <table className="table table-zebra w-full text-sm">
                <thead>
                  <tr>
                    <th className="font-medium text-gray-600 dark:text-white/70">
                      {intl.formatMessage({ id: 'DASHBOARD.RECENT.GUEST' })}
                    </th>
                    <th className="font-medium text-gray-600 dark:text-white/70">
                      {intl.formatMessage({ id: 'DASHBOARD.RECENT.HARBOR' })}
                    </th>
                    <th className="font-medium text-gray-600 dark:text-white/70">
                      {intl.formatMessage({ id: 'DASHBOARD.RECENT.BERTH' })}
                    </th>
                    <th className="font-medium text-gray-600 dark:text-white/70">
                      {intl.formatMessage({ id: 'DASHBOARD.RECENT.DATES' })}
                    </th>
                    <th className="font-medium text-gray-600 dark:text-white/70">
                      {intl.formatMessage({ id: 'DASHBOARD.RECENT.TOTAL' })}
                    </th>
                    <th className="font-medium text-gray-600 dark:text-white/70">
                      {intl.formatMessage({ id: 'DASHBOARD.RECENT.STATUS' })}
                    </th>
                    <th>
                      <span className="sr-only">
                        {intl.formatMessage({ id: 'DASHBOARD.RECENT.ACTIONS' })}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.recent_bookings.map((booking) => (
                    <tr key={booking.id}>
                      <td>
                        {booking.guest_name || booking.guest_email || '-'}
                      </td>
                      <td>{booking.harbor_name || '-'}</td>
                      <td>{booking.berth_number || '-'}</td>
                      <td>
                        {formatDisplayDate(booking.start_date_local)} –{' '}
                        {formatDisplayDate(booking.end_date_local)}
                      </td>
                      <td>{formatFinnishCurrency(booking.total_price)}</td>
                      <td>
                        <span
                          className={`text-sm font-medium ${getStatusClassName(booking.status)}`}
                        >
                          {intl.formatMessage({
                            id: getBookingStatusMessageId(booking.status),
                          })}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-xs btn-light"
                          onClick={() =>
                            navigate(`/booking-management/${booking.id}`, {
                              state: DASHBOARD_ORIGIN,
                            })
                          }
                        >
                          {intl.formatMessage({ id: 'DASHBOARD.RECENT.VIEW' })}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
    </Container>
  );
};

export { HarborRoleDashboard };
